import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  InputAdornment,
  Paper,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  FormControl,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../utils/formatters';
import BlockIcon from '@mui/icons-material/Block';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PaymentIcon from '@mui/icons-material/Payment';
import RefreshIcon from '@mui/icons-material/Refresh';
import Collapse from '@mui/material/Collapse';
// Removidos imports não utilizados: DatePicker, LocalizationProvider, AdapterDateFns
import ModalPagamento from '../Pagamentos/components/ModalPagamento';
import ModalVisualizarJuros from './components/ModalEditarJuros';
import AportesSection from './components/AportesSection';
import EditModal from './components/EditModal';
import HistoricoEdicoes from './components/HistoricoEdicoes';
import { listarAportes, adicionarAporte } from '../../services/api';

// Componente ParcelasAccordion
const ParcelasAccordion = ({ 
  emprestimo, 
  formatDate, 
  formatCurrency, 
  handleOpenHistorico, 
  handleOpenPagarModal,
  handleOpenEditarJuros,
  expanded 
}) => (
  <TableRow>
    <TableCell colSpan={6} style={{ paddingTop: 0, paddingBottom: 0 }}>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ margin: 1 }}>
          {/* Seção de Aportes */}
          <Box sx={{ mb: 3 }}>
            <AportesSection emprestimoId={emprestimo.id} />
          </Box>
          
          <Box sx={{ p: 2, mb: 3, bgcolor: '#f0f4f8', borderRadius: 2, border: '1px solid #d1d9e6' }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              📋 Resumo do Contrato Original
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Este empréstimo foi fechado com <strong>{emprestimo.quantidade_parcelas_original || emprestimo.quantidade_parcelas} parcelas</strong> no valor de <strong>{formatCurrency(emprestimo.valor_parcela_original)}</strong> cada.
            </Typography>
            {emprestimo.quantidade_parcelas > (emprestimo.quantidade_parcelas_original || emprestimo.quantidade_parcelas) && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                * Foram adicionadas {emprestimo.quantidade_parcelas - (emprestimo.quantidade_parcelas_original || emprestimo.quantidade_parcelas)} parcelas extras ao contrato original.
              </Typography>
            )}
          </Box>

          <Typography variant="h6" gutterBottom component="div">
            Detalhamento de Parcelas
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Juros</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Informações</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Ordenar as parcelas sempre por numero_parcela de forma crescente */}
              {[...emprestimo.parcelas]
                .sort((a, b) => Number(a.numero_parcela) - Number(b.numero_parcela))
                .map((parcela, index) => (
                <TableRow key={parcela.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">
                      {/* Sempre exibir o número da parcela pelo campo numero_parcela, nunca pelo id! */}
                      {parcela.numero_parcela}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(parcela.data_vencimento)}</TableCell>
                  {/* Exibir sempre o valor atual da parcela pelo campo valor */}
                  <TableCell>{formatCurrency(Number(parcela.valor || 0))}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {formatCurrency(Number(parcela.valor_juros || 0))}
                      </Typography>
                      {parcela.juros_aplicados_manualmente && (
                        <Typography variant="caption" color="warning.main" display="block">
                          Manual ({parcela.percentual_juros_aplicado || 0}%)
                        </Typography>
                      )}
                      {!parcela.juros_aplicados_manualmente && parcela.valor_juros > 0 && (
                        <Typography variant="caption" color="success.main" display="block">
                          Automático
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{parcela.status === 'pago_parcial' ? 'pago' : parcela.status}</TableCell>
                  <TableCell>{parcela.observacoes}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title="Visualizar Juros">
                        <IconButton
                          onClick={() => handleOpenEditarJuros(parcela)}
                          size="small"
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Histórico">
                        <IconButton
                          onClick={() => handleOpenHistorico(parcela.id)}
                          size="small"
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </TableCell>
  </TableRow>
);

export default function Emprestimos() {
  // Estados
  const [emprestimos, setEmprestimos] = useState([]);
  const [selectedEmprestimo, setSelectedEmprestimo] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [historicoPagamento, setHistoricoPagamento] = useState({});
  const [pagarModalOpen, setPagarModalOpen] = useState(false);

  // Estados de controle de UI e filtros
  const [openDialog, setOpenDialog] = useState(false);
  const [historicoDialogOpen, setHistoricoDialogOpen] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('ativo');
  const [expandedRows, setExpandedRows] = useState({});
  const [parcelasExpanded, setParcelasExpanded] = useState({});
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [filtrosAplicados, setFiltrosAplicados] = useState({
    dataInicial: '',
    dataFinal: '',
    status: 'ativo'
  });

  // Estado do formulário
  const [formData, setFormData] = useState({
    id_cliente: '',
    valor_total: '',
    quantidade_parcelas: '',
    data_primeiro_vencimento: format(new Date(), 'yyyy-MM-dd'),
    taxa_juros: '',
    periodicidade: 'mensal'
  });

  // Estados para aportes
  const [aportes, setAportes] = useState([]);
  const [aporteDialogOpen, setAporteDialogOpen] = useState(false);
  const [aporteForm, setAporteForm] = useState({ 
    valor_aporte: '', 
    data_aporte: '', 
    observacao: '',
    juros_manual: '',
    percentual_juros: '',
    tipo_juros: 'regra', // Agora padrão é 'regra'
    valor_regra_juros: 30,
    valor_regra_base: 150
  });
  const [emprestimoSelecionadoAporte, setEmprestimoSelecionadoAporte] = useState(null);
  const [aportesLoading, setAportesLoading] = useState(false);
  
  // Estados para edição de juros
  const [editarJurosModalOpen, setEditarJurosModalOpen] = useState(false);
  const [parcelaSelecionadaJuros, setParcelaSelecionadaJuros] = useState(null);

  // Estados para edição/renegociação
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [historicoEdicoesOpen, setHistoricoEdicoesOpen] = useState(false);
  const [emprestimoEditando, setEmprestimoEditando] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Funções auxiliares
  const formatDate = (date) => {
    if (!date) return '-';
    try {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) return '-';
      return format(parsedDate, 'dd/MM/yyyy', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '-';
    }
  };
  
  // Formata data para o formato do backend (YYYY-MM-DD)
  const formatDateForBackend = (date) => {
    if (!date) return '';
    try {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) return '';
      return format(parsedDate, 'yyyy-MM-dd');
    } catch (error) {
      console.error('Erro ao formatar data para o backend:', error);
      return '';
    }
  };

  const handleExpandRow = (emprestimoId) => {
    setExpandedRows(prev => ({
      ...prev,
      [emprestimoId]: !prev[emprestimoId]
    }));
  };

  // Função para alternar expansão das parcelas na versão mobile
  const toggleParcelasExpanded = (emprestimoId) => {
    // Usar setTimeout para evitar problemas com ResizeObserver
    setTimeout(() => {
      setParcelasExpanded(prev => ({
        ...prev,
        [emprestimoId]: !prev[emprestimoId]
      }));
    }, 0);
  };

  // Filtrar empréstimos localmente (apenas para busca por nome)
  const filteredEmprestimos = useMemo(() => {
    if (!Array.isArray(emprestimos)) return [];
    if (filtrosAplicados.id_cliente) {
      return emprestimos.filter(e => String(e.id_cliente) === String(filtrosAplicados.id_cliente));
    }
    return emprestimos.filter(emprestimo => {
      // Filtra apenas por nome do cliente (busca local)
      const matchesSearch = !filtro || 
        (emprestimo?.cliente?.nome?.toLowerCase().includes(filtro.toLowerCase()));
      return matchesSearch;
    });
  }, [emprestimos, filtro, filtrosAplicados.id_cliente]);

  // Sincroniza statusFiltro com filtrosAplicados
  useEffect(() => {
    console.log('Atualizando filtrosAplicados com status:', statusFiltro);
    setFiltrosAplicados(prev => ({
      ...prev,
      status: statusFiltro
    }));
  }, [statusFiltro]);

  // Log para depuração
  useEffect(() => {
    console.log('Empréstimos carregados:', emprestimos?.length || 0);
    console.log('Empréstimos filtrados:', filteredEmprestimos?.length || 0);
    console.log('Filtros aplicados:', filtrosAplicados);
  }, [emprestimos, filteredEmprestimos, filtrosAplicados]);

  // Funções de carregamento de dados
  const loadEmprestimos = useCallback(async (forceRefresh = false) => {
    try {
      const params = {};
      if (filtrosAplicados.id_cliente) {
        params.id_cliente = filtrosAplicados.id_cliente;
      }
      // Adiciona filtros de data se existirem, formatando corretamente
      if (filtrosAplicados.dataInicial) {
        params.data_inicial = formatDateForBackend(filtrosAplicados.dataInicial);
      }
      
      if (filtrosAplicados.dataFinal) {
        // Adiciona 1 dia para incluir a data final inteira
        const endDate = new Date(filtrosAplicados.dataFinal);
        endDate.setDate(endDate.getDate() + 1);
        params.data_final = formatDateForBackend(endDate);
      }

      // Força refresh sem cache se solicitado
      if (forceRefresh) {
        params._t = Date.now(); // Timestamp para evitar cache
      }

      // Enviamos filtros de status para o backend de acordo com a seleção
      // REMOVIDO: A filtragem de status agora é feita no frontend após o processamento
      /*
      if (filtrosAplicados.status) {
        if (filtrosAplicados.status === 'todos') {
          // Não adiciona filtro de status
        }
        else if (filtrosAplicados.status === 'quitado') {
          params.status = 'quitado';
        }
        else {
          params.status = filtrosAplicados.status;
        }
      }
      */
      
      console.log('Parâmetros enviados para a API:', params);
      const response = await api.get('/emprestimos', { params });
      console.log('Dados retornados pela API - Primeiro registro:', response.data[0]);
      // Verificar se os empréstimos têm o campo created_at
      const temCreatedAt = response.data[0]?.created_at !== undefined;
      console.log('Empréstimos têm campo created_at?', temCreatedAt);
      
      // Processar o status dos empréstimos com base nas parcelas
      const processedEmprestimos = response.data.map(emp => {
        if (emp.parcelas && emp.parcelas.length > 0) {
          const allParcelsPaid = emp.parcelas.every(
            parcela => parcela.status === 'pago'
          );
          if (allParcelsPaid) {
            return { ...emp, status: 'quitado' };
          }
        }
        return emp; // Retorna o empréstimo original se não houver parcelas ou se nem todas estiverem pagas/parcialmente pagas
      });
      
      // Aplicar filtro de status
      let emprestimosFiltrados = processedEmprestimos;
      
      if (filtrosAplicados.status === 'todos') {
        // Mostra todos os empréstimos sem filtrar por status
        console.log('Mostrando todos os empréstimos (após processamento)');
        emprestimosFiltrados = [...processedEmprestimos]; // Garante que estamos usando uma cópia dos dados
      } else if (filtrosAplicados.status) {
        // Filtra por outros status (ativo, inativo, quitado)
        emprestimosFiltrados = processedEmprestimos.filter(emp => emp.status === filtrosAplicados.status);
        console.log(`Filtrado por status '${filtrosAplicados.status}': ${emprestimosFiltrados.length} empréstimos (após processamento)`);
      }
      
      setEmprestimos(emprestimosFiltrados);
    } catch (error) {
      console.error('Erro ao carregar empréstimos:', error);
      toast.error('Erro ao carregar empréstimos');
    }
  }, [filtrosAplicados]);
  
  // Função para limpar todos os filtros
  const limparFiltros = () => {
    // Simplesmente limpar os estados dos filtros
    setDataInicial('');
    setDataFinal('');
    setFiltro('');
    setStatusFiltro('ativo');
    
    // Atualizar filtros aplicados - isso desencadeia o useEffect que carrega os dados
    setFiltrosAplicados({
      dataInicial: '',
      dataFinal: '',
      status: 'ativo'
    });
    
    // Mensagem de notificação em azul claro
    toast.info('Filtros limpos');
  };

  // Funções do modal de pagamento
  const handleOpenPagarModal = useCallback((parcela) => {
    setPagarModalOpen(true);
  }, []);

  const handleClosePagarModal = () => {
    setPagarModalOpen(false);
  };

  // Funções removidas por não serem utilizadas: handlePagarSubmit, handlePagarInputChange

  const loadClientes = useCallback(async () => {
    try {
      const response = await api.get('/clientes', { params: { ativo: true } });
      setClientes(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    }
  }, []);

  // Atualiza os filtros aplicados quando os filtros são alterados
  useEffect(() => {
    setFiltrosAplicados({
      dataInicial,
      dataFinal,
      status: statusFiltro
    });
  }, [dataInicial, dataFinal, statusFiltro]);

  // Carrega dados iniciais e quando os filtros mudam
  useEffect(() => {
    console.log('Filtros aplicados mudaram, carregando empréstimos...');
    loadEmprestimos();
    loadClientes();
  }, [filtrosAplicados, loadEmprestimos, loadClientes]);

  // UseEffect para verificar se deve abrir o modal de pagamentos automaticamente
  useEffect(() => {
    if (location.state?.abrirModal && location.state?.clienteSelecionado) {
      const cliente = location.state.clienteSelecionado;
      console.log('Emprestimos useEffect: Abrindo modal automaticamente para cliente:', cliente);
      
      // Buscar o empréstimo do cliente e abrir o modal
      const buscarEmprestimoECarregar = async () => {
        try {
          const response = await api.get(`/emprestimos?cliente_id=${cliente.id}&status=ativo&incluir_parcelas=true`);
          const emprestimos = response.data;
          
          if (emprestimos.length > 0) {
            const emprestimo = emprestimos[0];
            setSelectedEmprestimo(emprestimo);
            setPagarModalOpen(true);
            
            // Limpar o estado da navegação
            window.history.replaceState({}, document.title);
          } else {
            toast.error('Nenhum empréstimo ativo encontrado para este cliente');
          }
        } catch (error) {
          console.error('Erro ao buscar empréstimo:', error);
          toast.error('Erro ao abrir modal de pagamentos');
        }
      };
      
      buscarEmprestimoECarregar();
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.filtroClienteId) {
      setFiltro(''); // Limpa o filtro por nome
      setFiltrosAplicados(prev => ({
        ...prev,
        id_cliente: location.state.filtroClienteId
      }));
    }
  }, [location.state]);

  // Handlers
  const handleOpenHistorico = async (parcelaId) => {
    try {
      const response = await api.get(`/pagamentos/${parcelaId}/historico`);
      if (response.data) {
        const historico = response.data;
        if (historico.parcela) {
          historico.valor_original = historico.parcela.valor;
          // Usar sempre o valor total de juros automáticos do backend
          historico.valor_juros_automaticos = historico.total_juros_automaticos;

          // Calcular percentuais SOMENTE se não vierem do backend
          if (historico.valor_original) {
            if (historico.total_juros_automaticos_percentual === undefined) {
              historico.total_juros_automaticos_percentual = ((historico.valor_juros_automaticos || 0) / historico.valor_original * 100).toFixed(2);
            }
            if (!historico.juros_manuais_percentual) {
              historico.juros_manuais_percentual = ((historico.total_juros || 0) / historico.valor_original * 100).toFixed(2);
            }
            if (!historico.desconto_percentual) {
              historico.desconto_percentual = ((historico.total_desconto || 0) / historico.valor_original * 100).toFixed(2);
            }
          }
        }
        setHistoricoPagamento(historico);
        setHistoricoDialogOpen(true);
      } else {
        toast.info('Não há histórico de pagamentos para esta parcela');
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      toast.error('Erro ao buscar histórico de pagamento');
    }
  };

  const handleInativarEmprestimo = async (emprestimo) => {
    try {
      await api.put(`/emprestimos/${emprestimo.id}/inativar`);
      loadEmprestimos(true); // Força refresh após inativar
      toast.success('Empréstimo inativado com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao inativar empréstimo');
    }
  };

  const handleReativarEmprestimo = async (emprestimo) => {
    try {
      await api.put(`/emprestimos/${emprestimo.id}/reativar`);
      loadEmprestimos(true); // Força refresh após reativar
      toast.success('Empréstimo reativado com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao reativar empréstimo');
    }
  };

  const handleOpenDialog = (emprestimo = null) => {
    if (emprestimo) {
      setSelectedEmprestimo(emprestimo);
      setFormData({
        id_cliente: emprestimo.id_cliente,
        valor_total: emprestimo.valor_total,
        quantidade_parcelas: emprestimo.quantidade_parcelas,
        data_primeiro_vencimento: emprestimo.data_primeiro_vencimento
          ? format(new Date(emprestimo.data_primeiro_vencimento), 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd'),
        taxa_juros: emprestimo.taxa_juros,
        periodicidade: emprestimo.periodicidade || 'mensal'
      });
    } else {
      setSelectedEmprestimo(null);
      setFormData({
        id_cliente: '',
        valor_total: '',
        quantidade_parcelas: '',
        data_primeiro_vencimento: format(new Date(), 'yyyy-MM-dd'),
        taxa_juros: '',
        periodicidade: 'mensal'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEmprestimo(null);
    setFormData({
      id_cliente: '',
      valor_total: '',
      quantidade_parcelas: '',
      data_primeiro_vencimento: format(new Date(), 'yyyy-MM-dd'),
      taxa_juros: '',
      periodicidade: 'mensal'
    });
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMenuId, setSelectedMenuId] = useState(null);

  const handleMenuClick = (event, emprestimo) => {
    event.stopPropagation(); // Evita propagação do evento
    setAnchorEl(event.currentTarget);
    setSelectedEmprestimo(emprestimo);
    setSelectedMenuId(emprestimo.id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMenuId(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const data = {
        ...formData,
        valor_total: Number(formData.valor_total),
        quantidade_parcelas: Number(formData.quantidade_parcelas),
        taxa_juros: Number(formData.taxa_juros),
      };

      if (selectedEmprestimo) {
        await api.put(`/emprestimos/${selectedEmprestimo.id}`, data);
        toast.success('Empréstimo atualizado com sucesso');
      } else {
        await api.post('/emprestimos', data);
        toast.success('Empréstimo criado com sucesso');
      }

      handleCloseDialog();
      loadEmprestimos(true); // Força refresh após criar/editar empréstimo
    } catch (error) {
      console.error('Erro ao salvar empréstimo:', error);
      toast.error('Erro ao salvar empréstimo');
    }
  };

  useEffect(() => {
    if (filteredEmprestimos.length === 1) {
      setExpandedRows({ [filteredEmprestimos[0].id]: true });
    }
  }, [filteredEmprestimos]);

  const handleOpenAporteDialog = async (emprestimo) => {
    setEmprestimoSelecionadoAporte(emprestimo);
    setAporteDialogOpen(true);
    setAportesLoading(true);
    try {
      const resp = await api.get(`/emprestimos/${emprestimo.id}/aportes-detalhados`);
      setAportes(resp.data);
      
      // Buscar regras de juros para ver se tem uma de Aporte ativa
      const rulesResp = await api.get('/regras-juros');
      const rule = rulesResp.data.find(r => r.base_calculo === 'aporte' && r.ativo);
      if (rule) {
        setAporteForm(prev => {
          const newState = {
            ...prev,
            tipo_juros: 'regra',
            valor_regra_juros: rule.taxa,
            valor_regra_base: rule.valor_minimo
          };
          return newState;
        });
      }
    } catch (e) {
      console.error('Erro ao buscar aportes ou regras:', e);
      toast.error('Erro ao carregar dados do aporte');
    } finally {
      setAportesLoading(false);
    }
  };

  const handleCloseAporteDialog = () => {
    setAporteDialogOpen(false);
    setAporteForm({ 
      valor_aporte: '', 
      data_aporte: '', 
      observacao: '',
      juros_manual: '',
      percentual_juros: '',
      tipo_juros: 'regra',
      valor_regra_juros: 30,
      valor_regra_base: 150
    });
    setAportes([]);
    setEmprestimoSelecionadoAporte(null);
    loadEmprestimos(true);
    if (selectedEmprestimo) {
      api.get(`/emprestimos/${selectedEmprestimo.id}`).then(resp => {
        if (resp.data) setSelectedEmprestimo(resp.data);
      });
    }
  };

  const handleAporteFormChange = (e) => {
    const { name, value } = e.target;
    setAporteForm(prev => {
      const newState = { ...prev, [name]: value };
      
      // Recalcular juros se for regra proporcional
      if (newState.tipo_juros === 'regra' && (name === 'valor_aporte' || name === 'valor_regra_juros' || name === 'valor_regra_base')) {
        const valor = parseFloat(newState.valor_aporte) || 0;
        const jurosBase = parseFloat(newState.valor_regra_juros) || 0;
        const valorBase = parseFloat(newState.valor_regra_base) || 1; // Evitar divisão por zero
        newState.juros_manual = (valor / valorBase * jurosBase).toFixed(2);
      }
      
      return newState;
    });
  };

  const handleAdicionarAporte = async () => {
    if (!aporteForm.valor_aporte || !aporteForm.data_aporte) {
      toast.error('Preencha valor e data do aporte');
      return;
    }
    try {
      await adicionarAporte(emprestimoSelecionadoAporte.id, aporteForm);
      toast.success('Aporte adicionado com sucesso!');
      // Atualiza lista de aportes
      const resp = await listarAportes(emprestimoSelecionadoAporte.id);
      setAportes(resp.data);
      setAporteForm({ 
        valor_aporte: '', 
        data_aporte: '', 
        observacao: '',
        juros_manual: '',
        percentual_juros: '',
        tipo_juros: 'valor'
      });
      loadEmprestimos(true); // Força refresh após aporte
    } catch (e) {
      toast.error('Erro ao adicionar aporte');
    }
  };

  const handleOpenEditarJuros = (parcela) => {
    setParcelaSelecionadaJuros(parcela);
    setEditarJurosModalOpen(true);
  };

  // Função removida - não é mais necessária para visualização

  // Renderização do componente
  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho com busca e filtros */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, p: 1, '@media (max-width: 768px)': { flexDirection: 'column', alignItems: 'flex-start' } }}>
        {/* Campo de busca */}
        <TextField
          size="small"
          label="Cliente"
          variant="outlined"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ color: 'text.secondary', mr: 0.5 }} fontSize="small" />
            ),
          }}
          sx={{ width: 180, flexShrink: 0, '@media (max-width: 768px)': { width: '100%', mb: 1 } }}
        />
        
        {/* Seletor de Status */}
        <TextField
          select
          size="small"
          label="Status"
          value={statusFiltro}
          onChange={(e) => {
            console.log('Status alterado para:', e.target.value);
            setStatusFiltro(e.target.value);
          }}
          sx={{ width: 150, flexShrink: 0, '@media (max-width: 768px)': { width: '100%', mb: 1 } }}
          variant="outlined"
        >
          <MenuItem value="todos">Todos</MenuItem>
          <MenuItem value="ativo">Ativos</MenuItem>
          <MenuItem value="inativo">Inativos</MenuItem>
          <MenuItem value="quitado">Quitados</MenuItem>
        </TextField>
        
        {/* Campo de Data Inicial */}
        <TextField
          size="small"
          label="De"
          type="date"
          value={dataInicial}
          onChange={(e) => setDataInicial(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ width: 140, flexShrink: 0, '@media (max-width: 768px)': { width: '100%', mb: 1 } }}
          variant="outlined"
        />
        
        {/* Campo de Data Final */}
        <TextField
          size="small"
          label="Até"
          type="date"
          value={dataFinal}
          onChange={(e) => setDataFinal(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ width: 140, flexShrink: 0, '@media (max-width: 768px)': { width: '100%', mb: 1 } }}
          variant="outlined"
        />
        
        {/* Container para os botões de ação */}
        <Box sx={{ display: 'flex', gap: 1, '@media (max-width: 768px)': { width: '100%', justifyContent: 'space-between' } }}>
          {/* Botão Limpar */}
          <Button 
            variant="contained" 
            color="warning" 
            size="small"
            onClick={limparFiltros}
            sx={{ width: 90, flexShrink: 0, '@media (max-width: 768px)': { width: '48%' } }}
          >
            LIMPAR
          </Button>
          
          {/* Botão Aplicar */}
          <Button 
            variant="contained" 
            color="primary" 
            size="small"
            onClick={async () => {
              // Apenas atualizamos o estado filtrosAplicados e deixamos o useEffect fazer o resto
              setFiltrosAplicados({
                dataInicial,
                dataFinal,
                status: statusFiltro
              });
              // Mostrar mensagem informativa
              toast.info('Aplicando filtros...');
            }}
            sx={{ width: 90, flexShrink: 0, '@media (max-width: 768px)': { width: '48%' } }}
          >
            APLICAR
          </Button>
          
          {/* Botão Refresh */}
          <Button 
            variant="outlined" 
            color="secondary" 
            size="small"
            onClick={async () => {
              toast.info('Atualizando dados...');
              await loadEmprestimos(true);
              toast.success('Dados atualizados!');
            }}
            sx={{ width: 90, flexShrink: 0, '@media (max-width: 768px)': { width: '48%' } }}
            startIcon={<RefreshIcon />}
          >
            ATUALIZAR
          </Button>
        </Box>
        
        {/* Botão Novo */}
        <Button
          variant="contained"
          color="success"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="small"
          sx={{ minWidth: 70, flexShrink: 0, marginLeft: 'auto', '@media (max-width: 768px)': { width: '100%', mt: 2, marginLeft: 0 } }}
        >
          NOVO
        </Button>
      </Box>

      {/* Tabela de Empréstimos */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {filteredEmprestimos.map((emprestimo) => (
          <Box key={emprestimo.id} sx={{ mb: 3, p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                Cliente: {emprestimo.cliente?.nome}
              </Typography>
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ ml: 1 }}
                onClick={() => handleOpenAporteDialog(emprestimo)}
              >
                Novo Aporte
              </Button>
              <Tooltip title="Registrar Pagamento">
                <IconButton size="small" color="primary" onClick={() => {
                  navigate('/pagamentos', {
                    state: {
                      filtroEmprestimoId: emprestimo.id,
                      abrirModal: true
                    }
                  });
                }}>
                  <PaymentIcon />
                </IconButton>
              </Tooltip>
              {/* Botões de Inativar/Reativar para Mobile */}
              {emprestimo.status === 'inativo' ? (
                <Tooltip title="Reativar Empréstimo">
                  <IconButton 
                    size="small" 
                    color="success" 
                    onClick={() => handleReativarEmprestimo(emprestimo)}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Inativar Empréstimo">
                  <IconButton 
                    size="small" 
                    color="warning" 
                    onClick={() => handleInativarEmprestimo(emprestimo)}
                  >
                    <BlockIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" mb={1}>
              Valor Total: {formatCurrency(emprestimo.valor_total)} | Taxa: {emprestimo.taxa_juros}% | Parcelas: {emprestimo.quantidade_parcelas_original || emprestimo.quantidade_parcelas}
            </Typography>
            <AportesSection emprestimoId={emprestimo.id} />
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                Parcelas
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => toggleParcelasExpanded(emprestimo.id)}
                endIcon={parcelasExpanded[emprestimo.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                {parcelasExpanded[emprestimo.id] ? 'Recolher' : 'Expandir'}
              </Button>
            </Box>
            <Collapse in={parcelasExpanded[emprestimo.id]} timeout="auto" unmountOnExit>
              {emprestimo.parcelas?.sort((a, b) => Number(a.numero_parcela) - Number(b.numero_parcela)).map((parcela) => {
              const isAtrasada = parcela.status === 'atrasado';
              return (
                <Box key={parcela.id} sx={{ mb: 2, p: 2, bgcolor: isAtrasada ? '#ffebee' : '#f5f5f5', borderRadius: 2, boxShadow: 0 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: isAtrasada ? 'error.main' : 'text.primary', mr: 1 }}>
                      Parcela {parcela.numero_parcela} {isAtrasada && <span style={{ fontWeight: 'normal', fontSize: '0.95em', marginLeft: 6 }}>(ATRASADA)</span>}
                    </Typography>
                    <Box flex={1} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(parcela.data_vencimento)}
                    </Typography>
                  </Box>
                  <Typography variant="body2">Valor: {formatCurrency(parcela.valor)}</Typography>
                  <Typography variant="body2">Status: <span style={{ color: isAtrasada ? '#d32f2f' : undefined }}>{parcela.status.toUpperCase()}</span></Typography>
                  <Typography variant="body2">Informações: {parcela.observacoes || '-'}</Typography>
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    <Button
                      color="primary"
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenHistorico(parcela.id)}
                      startIcon={<HistoryIcon />}
                      sx={{ fontSize: '0.95em' }}
                    >
                      Histórico
                    </Button>
                  </Box>
                </Box>
              );
            })}
            </Collapse>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Valor Total</TableCell>
                <TableCell>Taxa de Juros</TableCell>
                <TableCell>Parcelas</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data de Criação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmprestimos.map((emprestimo) => (
                <React.Fragment key={emprestimo.id}>
                  <TableRow>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => handleExpandRow(emprestimo.id)}
                        >
                          {expandedRows[emprestimo.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                        {emprestimo.cliente?.nome}
                        <Button
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ ml: 1 }}
                          onClick={() => handleOpenAporteDialog(emprestimo)}
                        >
                          Novo Aporte
                        </Button>
                        <Tooltip title="Registrar Pagamento">
                          <IconButton size="small" color="primary" onClick={() => {
                            navigate('/pagamentos', {
                              state: {
                                filtroEmprestimoId: emprestimo.id,
                                abrirModal: true
                              }
                            });
                          }}>
                            <PaymentIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>{formatCurrency(emprestimo.valor_total)}</TableCell>
                    <TableCell>{emprestimo.taxa_juros}%</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {emprestimo.quantidade_parcelas_original || emprestimo.quantidade_parcelas}
                        {emprestimo.quantidade_parcelas > (emprestimo.quantidade_parcelas_original || emprestimo.quantidade_parcelas) && (
                          <span style={{ color: 'red', fontWeight: 'bold' }}>
                            {' '}(+{emprestimo.quantidade_parcelas - (emprestimo.quantidade_parcelas_original || emprestimo.quantidade_parcelas)})
                          </span>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>{emprestimo.status}</TableCell>
                    <TableCell>{formatDate(emprestimo.created_at || emprestimo.data_criacao || (emprestimo.parcelas && emprestimo.parcelas.length > 0 ? emprestimo.parcelas[0].data_criacao : null))}</TableCell>
                    <TableCell align="right">
                      <Box component="div" sx={{ display: 'inline-block' }}>
                        <IconButton 
                          onClick={(event) => handleMenuClick(event, emprestimo)}
                          aria-controls={`menu-${emprestimo.id}`}
                          aria-haspopup="true"
                          id={`menu-button-${emprestimo.id}`}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                      
                      <Menu
                        id={`menu-${emprestimo.id}`}
                        anchorEl={anchorEl}
                        open={selectedMenuId === emprestimo.id && Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        MenuListProps={{
                          'aria-labelledby': `menu-button-${emprestimo.id}`,
                        }}
                        disablePortal
                        keepMounted
                      >
                        {selectedEmprestimo?.status === 'inativo' ? (
                          <MenuItem
                            onClick={() => {
                              handleMenuClose();
                              handleReativarEmprestimo(selectedEmprestimo);
                            }}
                          >
                            <ListItemIcon>
                              <PlayArrowIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Reativar Empréstimo</ListItemText>
                          </MenuItem>
                        ) : (
                          <MenuItem
                            onClick={() => {
                              handleMenuClose();
                              handleInativarEmprestimo(selectedEmprestimo);
                            }}
                          >
                            <ListItemIcon>
                              <BlockIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Inativar Empréstimo</ListItemText>
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={() => {
                            handleMenuClose();
                            setEmprestimoEditando(selectedEmprestimo);
                            setEditModalOpen(true);
                          }}
                        >
                          <ListItemIcon>
                            <EditIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Renegociar (Editar)</ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleMenuClose();
                            setEmprestimoEditando(selectedEmprestimo);
                            setHistoricoEdicoesOpen(true);
                          }}
                        >
                          <ListItemIcon>
                            <HistoryIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Histórico de Edições</ListItemText>
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                  <ParcelasAccordion
                    key={emprestimo.id}
                    emprestimo={emprestimo}
                    formatDate={formatDate}
                    formatCurrency={formatCurrency}
                    handleOpenHistorico={handleOpenHistorico}
                    handleOpenPagarModal={handleOpenPagarModal}
                    handleOpenEditarJuros={handleOpenEditarJuros}
                    expanded={expandedRows[emprestimo.id]}
                  />
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Dialog do Histórico */}
      <Dialog
        open={historicoDialogOpen}
        onClose={() => setHistoricoDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            minWidth: 900,
            minHeight: 500,
            overflowX: 'auto',
            width: window.innerWidth <= 600 ? '98vw' : undefined,
            maxWidth: window.innerWidth <= 600 ? '98vw' : undefined,
            maxHeight: window.innerWidth <= 600 ? '90vh' : undefined,
            borderRadius: window.innerWidth <= 600 ? 12 : undefined,
            margin: window.innerWidth <= 600 ? 'auto' : undefined,
            padding: window.innerWidth <= 600 ? 8 : undefined,
          }
        }}
      >
        <DialogTitle sx={{ color: 'green', textAlign: window.innerWidth <= 600 ? 'center' : 'left' }}>Histórico de Pagamento</DialogTitle>
        {console.log('[Dialog Histórico] Dados recebidos:', historicoPagamento)}
        <DialogContent
          sx={{
            p: { xs: 1, sm: 2 },
            width: window.innerWidth <= 600 ? '96vw' : 'auto',
            maxWidth: window.innerWidth <= 600 ? '96vw' : 'auto',
            maxHeight: window.innerWidth <= 600 ? '70vh' : '80vh',
            overflowY: 'auto',
            bgcolor: window.innerWidth <= 600 ? '#fff' : 'inherit',
            borderRadius: window.innerWidth <= 600 ? 2 : 0,
            m: window.innerWidth <= 600 ? 'auto' : 0
          }}
        >
          {historicoPagamento && historicoPagamento.parcela ? (
            <div>
              <Typography gutterBottom><b>Cliente:</b> {historicoPagamento.parcela.emprestimo?.cliente?.nome || 'N/A'}</Typography>
              <Typography gutterBottom><b>Valor Original:</b> {formatCurrency(historicoPagamento.parcela.valor_original || 0)}</Typography>
              <Typography gutterBottom><b>Juros Automáticos:</b> {formatCurrency(historicoPagamento.total_juros_automaticos !== undefined ? historicoPagamento.total_juros_automaticos : (historicoPagamento.pagamentos ? historicoPagamento.pagamentos.reduce((acc, p) => acc + (Number(p.juros_automaticos) || 0), 0) : 0))} ({(Number(historicoPagamento.total_juros_automaticos_percentual) || 0).toFixed(2)}%)</Typography>
              <Typography gutterBottom>
                <b>Juros Manuais:</b>{' '}
                {(() => {
                  let totalJurosManuais = 0;
                  
                  // 1. Juros manuais aplicados diretamente na parcela
                  if (historicoPagamento.parcela && historicoPagamento.parcela.juros_aplicados_manualmente) {
                    totalJurosManuais += Number(historicoPagamento.parcela.valor_juros || 0);
                  }
                  
                  // 2. Juros manuais dos pagamentos
                  if (historicoPagamento.pagamentos && historicoPagamento.pagamentos.length > 0) {
                    totalJurosManuais += historicoPagamento.pagamentos.reduce((acc, p) => acc + (Number(p.valor_juros) || 0), 0);
                  }
                  
                  // 3. Calcular percentual total
                  const valorOriginal = Number(historicoPagamento.parcela?.valor_original || 0);
                  const percentual = valorOriginal > 0 ? ((totalJurosManuais / valorOriginal) * 100).toFixed(2) : '0.00';
                  
                  return (
                    <>
                      {formatCurrency(totalJurosManuais)}{' '}
                      <b style={{ color: '#e57373' }}>({percentual}%)</b>
                    </>
                  );
                })()}
              </Typography>
              <Typography gutterBottom><b>Desconto:</b> {formatCurrency(historicoPagamento.total_desconto || 0)} ({(Number(historicoPagamento.desconto_percentual) || 0).toFixed(2)}%)</Typography>
              <Typography gutterBottom><b><span style={{color:'#1976d2'}}>Total Pago:</span></b> <span style={{color:'#1976d2'}}>{formatCurrency(historicoPagamento.total_pago || 0)}</span></Typography>
              <Typography gutterBottom><b>Último Pagamento:</b> {historicoPagamento.pagamentos && historicoPagamento.pagamentos.length > 0 ? formatCurrency(historicoPagamento.pagamentos[historicoPagamento.pagamentos.length-1].valor_pago) : formatCurrency(0)}</Typography>
              <Typography gutterBottom><b>Data do Último Pagamento:</b> {historicoPagamento.pagamentos && historicoPagamento.pagamentos.length > 0 ? formatDate(historicoPagamento.pagamentos[historicoPagamento.pagamentos.length-1].data_pagamento) : '-'}</Typography>
              <Typography gutterBottom><b>Observações:</b> {historicoPagamento.parcela.observacoes || '-'}</Typography>
              
              {/* Informações sobre juros manuais da parcela */}
              {historicoPagamento.parcela && historicoPagamento.parcela.juros_aplicados_manualmente && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
                  <Typography variant="subtitle2" color="warning.main" gutterBottom>
                    💡 Juros Manuais Aplicados na Parcela
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <b>Valor:</b> {formatCurrency(historicoPagamento.parcela.valor_juros || 0)}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <b>Percentual:</b> {Number(historicoPagamento.parcela.percentual_juros_aplicado || 0).toFixed(2)}%
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <b>Data de Aplicação:</b> {historicoPagamento.parcela.data_aplicacao_juros ? formatDate(historicoPagamento.parcela.data_aplicacao_juros) : '-'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <b>Observações:</b> {historicoPagamento.parcela.observacoes_juros || '-'}
                  </Typography>
                </Box>
              )}
              
              <Box mt={4}>
                <Typography variant="h6" gutterBottom>Detalhamento dos Pagamentos</Typography>
                {/* MOBILE: Cards/blocos para cada pagamento */}
                {window.innerWidth <= 600 ? (
                  historicoPagamento.pagamentos && historicoPagamento.pagamentos.length > 0 ? (
                    historicoPagamento.pagamentos.map((pagamento) => (
                      <Box key={pagamento.id} sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, boxShadow: 1 }}>
                        <Typography variant="body2"><b>Data:</b> {formatDate(pagamento.data_pagamento)}</Typography>
                        <Typography variant="body2"><b>Valor Pago:</b> {formatCurrency(pagamento.valor_pago || 0)}</Typography>
                        <Typography variant="body2"><b>Juros:</b> {formatCurrency(pagamento.valor_juros || 0)}</Typography>
                        <Typography variant="body2"><b>Desconto:</b> {formatCurrency(pagamento.valor_desconto || 0)}</Typography>
                        <Typography variant="body2"><b>Observações:</b> {pagamento.observacoes || '-'}</Typography>
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#fffde7', borderRadius: 2, boxShadow: 1, border: '1px solid #ffe082' }}>
                      <Typography variant="body2" fontWeight="bold" color="warning.main">
                        Nenhum pagamento registrado
                      </Typography>
                    </Box>
                  )
                ) : (
                  // DESKTOP: Tabela tradicional
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Data</TableCell>
                          <TableCell>Valor Pago</TableCell>
                          <TableCell>Juros (%)</TableCell>
                          <TableCell>Desconto</TableCell>
                          <TableCell>Observações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historicoPagamento.pagamentos && historicoPagamento.pagamentos.length > 0 ? (
                          historicoPagamento.pagamentos.map((pagamento) => (
                            <TableRow key={pagamento.id}>
                              <TableCell>{formatDate(pagamento.data_pagamento)}</TableCell>
                              <TableCell>{formatCurrency(pagamento.valor_pago || 0)}</TableCell>
                              <TableCell>{formatCurrency(pagamento.valor_juros || 0)} <b style={{color: '#e57373'}}>{pagamento.juros_percentual > 0 ? `(${Number(pagamento.juros_percentual).toFixed(2)}%)` : ''}</b></TableCell>
                              <TableCell>{formatCurrency(pagamento.valor_desconto || 0)}</TableCell>
                              <TableCell>{pagamento.observacoes || '-'}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              Nenhum pagamento registrado
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </div>
          ) : historicoPagamento?.mensagem ? (
            <Typography>{historicoPagamento.mensagem}</Typography>
          ) : (
            <Typography>Carregando histórico...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoricoDialogOpen(false)} color="primary">FECHAR</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Criação/Edição de Empréstimo */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedEmprestimo ? 'Editar Empréstimo' : 'Novo Empréstimo'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Cliente"
                  name="id_cliente"
                  value={formData.id_cliente}
                  onChange={handleInputChange}
                  required
                >
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Valor Total"
                  name="valor_total"
                  type="number"
                  value={formData.valor_total}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Taxa de Juros"
                  name="taxa_juros"
                  type="number"
                  value={formData.taxa_juros}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantidade de Parcelas"
                  name="quantidade_parcelas"
                  type="number"
                  value={formData.quantidade_parcelas}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Data Primeiro Vencimento"
                  name="data_primeiro_vencimento"
                  type="date"
                  value={formData.data_primeiro_vencimento || ''}
                  onChange={e => setFormData({ ...formData, data_primeiro_vencimento: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Periodicidade"
                  name="periodicidade"
                  value={formData.periodicidade}
                  onChange={handleInputChange}
                  required
                >
                  <MenuItem value="mensal">Mensal</MenuItem>
                  <MenuItem value="quinzenal">Quinzenal</MenuItem>
                  <MenuItem value="semanal">Semanal</MenuItem>
                  <MenuItem value="diario">Diário</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {selectedEmprestimo ? 'Salvar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Modal de Pagamento */}
      <ModalPagamento
        open={pagarModalOpen}
        onClose={handleClosePagarModal}
        emprestimo={selectedEmprestimo}
        parcelasSelecionadas={selectedEmprestimo?.parcelas?.map(parcela => ({
          ...parcela,
          selected: false
        })) || []}
        jurosPercentual={0}
        setJurosPercentual={() => {}}
        descontoPercentual={0}
        setDescontoPercentual={() => {}}
        valoresPagamento={{}}
        setValoresPagamento={() => {}}
        novasDatasVencimento={{}}
        setNovasDatasVencimento={() => {}}
        observacoes=""
        setObservacoes={() => {}}
        handlePagamento={async (pagamentoData) => {
          try {
            await api.post(`/emprestimos/${selectedEmprestimo.id}/pagamentos`, pagamentoData);
            toast.success('Pagamento realizado com sucesso!');
            handleClosePagarModal();
            loadEmprestimos(true); // Força refresh após pagamento
          } catch (error) {
            console.error('Erro ao realizar pagamento:', error);
            toast.error('Erro ao realizar pagamento');
          }
        }}
        handleParcelaSelection={() => {}}
        aplicarJurosAutomaticos={false}
        setAplicarJurosAutomaticos={() => {}}
        emprestimos={[]}
        selecionarTodasParcelas={() => {}}
        removerTodasSelecoes={() => {}}
        valoresCalculados={{}}
        setValoresCalculados={() => {}}
        tipoJuros="percentual"
        setTipoJuros={() => {}}
        tipoDesconto="percentual"
        setTipoDesconto={() => {}}
        valorJurosFixo={0}
        setValorJurosFixo={() => {}}
        valorDescontoFixo={0}
        setValorDescontoFixo={() => {}}
        handleValorPagamentoChange={() => {}}
        handleJurosAutomaticosChange={() => {}}
      />

      {/* Dialog de Aportes */}
      <Dialog open={aporteDialogOpen} onClose={handleCloseAporteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Histórico de Aportes</DialogTitle>
        <DialogContent>
          {aportesLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box mb={2}>
                <TextField
                  label="Valor do Aporte"
                  name="valor_aporte"
                  type="number"
                  value={aporteForm.valor_aporte}
                  onChange={handleAporteFormChange}
                  fullWidth
                  margin="dense"
                />
                <TextField
                  label="Data do Aporte"
                  name="data_aporte"
                  type="date"
                  value={aporteForm.data_aporte}
                  onChange={handleAporteFormChange}
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Observação"
                  name="observacao"
                  value={aporteForm.observacao}
                  onChange={handleAporteFormChange}
                  fullWidth
                  margin="dense"
                />
                
                {/* Seção de Juros Manuais */}
                <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Juros Manuais no Aporte (Opcional)
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                    Juros automáticos só incidem nas parcelas quando estiverem atrasadas.
                  </Typography>
                  
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">Tipo de Juros</FormLabel>
                      <RadioGroup
                        row
                        name="tipo_juros"
                        value={aporteForm.tipo_juros}
                        onChange={(e) => {
                          const novoTipo = e.target.value;
                          setAporteForm(prev => {
                            const newState = {
                              ...prev,
                              tipo_juros: novoTipo,
                              juros_manual: '',
                              percentual_juros: ''
                            };
                            
                            // Forçar cálculo inicial ao mudar para regra
                            if (novoTipo === 'regra') {
                              const valor = parseFloat(newState.valor_aporte) || 0;
                              const jurosBase = parseFloat(newState.valor_regra_juros) || 30;
                              const valorBase = parseFloat(newState.valor_regra_base) || 150;
                              newState.juros_manual = (valor / valorBase * jurosBase).toFixed(2);
                            }
                            
                            return newState;
                          });
                        }}
                      >
                      <FormControlLabel value="valor" control={<Radio />} label="Valor Fixo" />
                      <FormControlLabel value="percentual" control={<Radio />} label="Percentual" />
                      <FormControlLabel value="regra" control={<Radio />} label="Regra Prop. (Filtro)" />
                    </RadioGroup>
                  </FormControl>
                  
                  {/* Atalhos de Valores Comuns */}
                  {(aporteForm.tipo_juros === 'valor' || aporteForm.tipo_juros === 'regra') && (
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {[10, 20, 30, 40, 50].map((v) => (
                        <Button
                          key={v}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            if (aporteForm.tipo_juros === 'valor') {
                              handleAporteFormChange({ target: { name: 'juros_manual', value: v.toString() } });
                            } else {
                              handleAporteFormChange({ target: { name: 'valor_regra_juros', value: v.toString() } });
                            }
                          }}
                        >
                          R$ {v}
                        </Button>
                      ))}
                    </Box>
                  )}
                  
                  {aporteForm.tipo_juros === 'valor' && (
                    <TextField
                      label="Valor dos Juros (R$)"
                      name="juros_manual"
                      type="number"
                      value={aporteForm.juros_manual}
                      onChange={handleAporteFormChange}
                      fullWidth
                      margin="dense"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                      }}
                    />
                  )}

                  {aporteForm.tipo_juros === 'percentual' && (
                    <TextField
                      label="Percentual de Juros (%)"
                      name="percentual_juros"
                      type="number"
                      value={aporteForm.percentual_juros}
                      onChange={handleAporteFormChange}
                      fullWidth
                      margin="dense"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  )}

                  {aporteForm.tipo_juros === 'regra' && (
                    <Box sx={{ mt: 1 }}>
                      <Box display="flex" gap={2} alignItems="center">
                        <TextField
                          label="Juros (R$)"
                          name="valor_regra_juros"
                          type="number"
                          value={aporteForm.valor_regra_juros}
                          onChange={handleAporteFormChange}
                          size="small"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                          }}
                        />
                        <Typography>por cada</Typography>
                        <TextField
                          label="Aporte (R$)"
                          name="valor_regra_base"
                          type="number"
                          value={aporteForm.valor_regra_base}
                          onChange={handleAporteFormChange}
                          size="small"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                          }}
                        />
                      </Box>
                      <TextField
                        label="Resultado (Valor Juros R$)"
                        name="juros_manual"
                        type="number"
                        value={aporteForm.juros_manual}
                        onChange={handleAporteFormChange}
                        fullWidth
                        margin="dense"
                        sx={{ mt: 2 }}
                        helperText="Calculado automaticamente (pode ser alterado)"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        }}
                      />
                    </Box>
                  )}
                </Box>
                <Button variant="contained" color="primary" onClick={handleAdicionarAporte} sx={{ mt: 2 }}>
                  Adicionar Aporte
                </Button>
              </Box>
              <Typography variant="subtitle1" gutterBottom>Histórico</Typography>
              {aportes.length === 0 ? (
                <Typography color="textSecondary">Nenhum aporte registrado</Typography>
              ) : (
                <>
                  {/* MOBILE: Cards/blocos para cada aporte */}
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {aportes.map((aporte) => (
                      <Box key={aporte.id} sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, boxShadow: 1 }}>
                        <Typography variant="body2"><b>Valor:</b> {formatCurrency(aporte.valor_aporte)}</Typography>
                        <Typography variant="body2"><b>Data:</b> {formatDate(aporte.data_aporte)}</Typography>
                        <Typography variant="body2"><b>Observação:</b> {aporte.observacao || '-'}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* DESKTOP: Tabela tradicional */}
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Valor</TableCell>
                          <TableCell>Data</TableCell>
                          <TableCell>Observação</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {aportes.map((aporte) => (
                          <TableRow key={aporte.id}>
                            <TableCell>{formatCurrency(aporte.valor_aporte)}</TableCell>
                            <TableCell>{formatDate(aporte.data_aporte)}</TableCell>
                            <TableCell>{aporte.observacao || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAporteDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal para editar juros das parcelas */}
      <ModalVisualizarJuros
        open={editarJurosModalOpen}
        onClose={() => setEditarJurosModalOpen(false)}
        parcela={parcelaSelecionadaJuros}
      />

      {/* Componentes de Edição de Empréstimo */}
      <EditModal 
        open={editModalOpen} 
        onClose={() => setEditModalOpen(false)} 
        emprestimo={emprestimoEditando} 
        onSuccess={() => loadEmprestimos(true)} 
      />
      <HistoricoEdicoes 
        open={historicoEdicoesOpen} 
        onClose={() => setHistoricoEdicoesOpen(false)} 
        emprestimoId={emprestimoEditando?.id} 
        onSuccess={() => loadEmprestimos(true)} 
      />
    </Box>
  );
}